import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useState, useEffect } from 'react';
import { useRequest } from 'ahooks';
import { Button, Space, Form, Input, Switch, Select, Spin } from 'udesk-ui';
import { Wrapper } from './components';
import Page from 'Component/common/udesk-qa-page-layout';
import WorkspacesSelect from './components/WorkspacesSelect';
import CoseAgentsSelect from './components/CoseAgentsSelect';
import TypeSelect, { TemplatePropsValue as TypeSelectValue } from './components/TypeSelect';
import DialogMessage from './components/DialogMessage';
import ParamsList, { TemplateProps as ParamsListProps } from './components/ParamsList';
import { postAiAgents } from 'src/api/aiAgents';
import { getAiAgentsById, putAiAgentsById } from 'src/api/aiAgents/{id}';
import { AiAgentCreateRequest } from 'src/api/types';
import { AiAgentTypeEnum } from '../const';
import { safeParse } from 'src/util/safeParse';

interface FormValuesUdesk
    extends Omit<
        AiAgentCreateRequest,
        'id' | 'aiAgentInfo' | 'type' | 'divideRule' | 'variateAssociateList' | 'status'
    > {
    source?: AiAgentTypeEnum.Udesk;
    api?: string;
    apiKey?: string;
    user?: string;
    typeSelect?: TypeSelectValue;
    variateAssociateList?: ParamsListProps['value'];
    status?: boolean;
}

interface FormValuesCoze
    extends Omit<
        AiAgentCreateRequest,
        'id' | 'aiAgentInfo' | 'type' | 'divideRule' | 'variateAssociateList' | 'status'
    > {
    source?: AiAgentTypeEnum.Coze;
    workspaceId?: string;
    botId?: string;
    typeSelect?: TypeSelectValue;
    variateAssociateList?: ParamsListProps['value'];
    status?: boolean;
}

type FormValues = FormValuesUdesk | FormValuesCoze;

const Template = React.memo((props: any) => {
    const id = props.match.params.id;

    const [initialValues, setInitialValues] = useState<FormValues>({
        status: false,
        source: AiAgentTypeEnum.Udesk,
        typeSelect: {
            type: 1,
        },
    });

    const [form] = Form.useForm<FormValues>();

    const { loading: getLoading } = useRequest(
        () =>
            getAiAgentsById({
                segments: {
                    id,
                },
            }),
        {
            ready: !!id,
            onSuccess: (res) => {
                const data: FormValues = {
                    ...res.data,
                    api: safeParse(res.data?.aiAgentInfo).api,
                    apiKey: safeParse(res.data?.aiAgentInfo).apiKey,
                    user: safeParse(res.data?.aiAgentInfo).user,
                    workspaceId: safeParse(res.data?.aiAgentInfo).workspaceId,
                    botId: safeParse(res.data?.aiAgentInfo).botId,
                    typeSelect: {
                        type: res.data?.type,
                        divideRuleList: res.data?.divideRuleList?.map((d) => {
                            return {
                                divide: d,
                            };
                        }),
                    },
                    status: res.data?.status === 1,
                    variateAssociateList: res.data?.variateAssociateList?.map((item) => {
                        return {
                            ...item,
                            type:
                                item.type === 1
                                    ? 1
                                    : item.type === 2
                                    ? 2
                                    : item.type === 3
                                    ? 3
                                    : undefined, // 确保类型为1、2或3
                        };
                    }),
                };

                setInitialValues((initialValues) => ({
                    ...initialValues,
                    ...data,
                }));
            },
        }
    );

    useEffect(() => {
        form.resetFields();
    }, [form, initialValues]);

    const onCancel = () => {
        let routeOptions: any = {
            history: props.history,
            routeName: 'aiAgentIndex',
            state: {},
        };
        Udesk.ui.routing.transitionTo(routeOptions);
    };

    const onWorkspaceChange = () => {
        form.setFieldsValue({
            botId: undefined,
        });
    };

    const [saveLoading, setSaveLoading] = useState(false);

    const onFinish = () => {
        form.validateFields().then(async () => {
            const values = form.getFieldsValue();

            const params: AiAgentCreateRequest = {
                agentName: values.agentName,
                agentDesc: values.agentDesc,
                source: values.source,
                aiAgentInfo:
                    values.source === AiAgentTypeEnum.Udesk
                        ? JSON.stringify({
                              api: values.api,
                              apiKey: values.apiKey,
                              user: values.user,
                          })
                        : values.source === AiAgentTypeEnum.Coze
                        ? JSON.stringify({
                              workspaceId: values.workspaceId,
                              botId: values.botId,
                          })
                        : undefined,
                type: values.typeSelect?.type,
                divideRuleList: values.typeSelect?.divideRuleList?.map((d) => d.divide ?? ''),
                dialogText: values.dialogText,
                status: values.status ? 1 : 0,
                variateAssociateList: values.variateAssociateList,
            };

            setSaveLoading(true);

            try {
                if (id) {
                    await putAiAgentsById(params, {
                        segments: {
                            id,
                        },
                        successMsg: UdeskLocales['current'].business.info.saveSuccess,
                    });
                } else {
                    await postAiAgents(params, {
                        successMsg: UdeskLocales['current'].business.info.creatSuccess,
                    });
                }
                onCancel();
            } catch (e) {
                console.log(e);
            } finally {
                setSaveLoading(false);
            }
        });
    };

    const loading = getLoading || saveLoading;

    return (
        <Page
            pageBodyClassName="wechat-staff-center-page-index"
            title={
                props.match.params.id
                    ? /* 编辑智能体 */ UdeskLocales['current'].pages.semanticIntelligence.aiAgent
                          .detail.index.editAgent
                    : /* 新建智能体 */ UdeskLocales['current'].pages.semanticIntelligence.aiAgent
                          .detail.index.newAgent
            }
            padding={true}
            onBack={onCancel}
        >
            <Wrapper>
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        labelCol={{ span: 4 }}
                        wrapperCol={{ span: 14 }}
                        initialValues={initialValues}
                    >
                        <Form.Item
                            label={
                                /* 智能体名称 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.agentName
                            }
                            name="agentName"
                            rules={[{ required: true }]}
                        >
                            <Input
                                maxLength={50}
                                placeholder={
                                    /* 请输入 */ UdeskLocales['current'].pages.semanticIntelligence
                                        .aiAgent.detail.index.pleaseEnter
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 智能体描述 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.agentDescription
                            }
                            name="agentDesc"
                            rules={[{ required: true }]}
                        >
                            <Input.TextArea
                                autoSize
                                placeholder={
                                    /* 请输入 */ UdeskLocales['current'].pages.semanticIntelligence
                                        .aiAgent.detail.index.pleaseEnter
                                }
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 有效性 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.effectiveness
                            }
                            name="status"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 来源 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.source
                            }
                            name="source"
                            rules={[{ required: true }]}
                        >
                            <Select
                                options={[
                                    {
                                        label: 'Udesk',
                                        value: AiAgentTypeEnum.Udesk,
                                    },
                                    {
                                        label: 'Coze',
                                        value: AiAgentTypeEnum.Coze,
                                    },
                                ]}
                                style={{ width: 200 }}
                                placeholder={UdeskLocales['current'].labels.pleaseSelect}
                            />
                        </Form.Item>

                        <Form.Item<FormValues> noStyle shouldUpdate>
                            {({ getFieldsValue }) => {
                                const source = getFieldsValue().source;

                                return source === AiAgentTypeEnum.Coze ? (
                                    <>
                                        <Form.Item
                                            label={
                                                /* 空间 */ UdeskLocales['current'].pages
                                                    .semanticIntelligence.aiAgent.detail.index.space
                                            }
                                            name="workspaceId"
                                            rules={[{ required: true }]}
                                        >
                                            <WorkspacesSelect onChange={onWorkspaceChange} />
                                        </Form.Item>

                                        <Form.Item<FormValuesCoze> noStyle shouldUpdate>
                                            {({ getFieldsValue }) => {
                                                const workspaceId = getFieldsValue().workspaceId;

                                                return (
                                                    <Form.Item
                                                        label={
                                                            /* 智能体 */ UdeskLocales['current']
                                                                .pages.semanticIntelligence.aiAgent
                                                                .detail.index.agent
                                                        }
                                                        name="botId"
                                                        rules={[{ required: true }]}
                                                    >
                                                        <CoseAgentsSelect
                                                            workspaceId={workspaceId}
                                                        />
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </>
                                ) : source === AiAgentTypeEnum.Udesk ? (
                                    <>
                                        <Form.Item
                                            label={
                                                /* API地址 */ UdeskLocales['current'].pages
                                                    .semanticIntelligence.aiAgent.detail.index
                                                    .aPIAddress
                                            }
                                            name="api"
                                            rules={[{ required: true }]}
                                        >
                                            <Input
                                                maxLength={50}
                                                placeholder={
                                                    /* 请输入 */ UdeskLocales['current'].pages
                                                        .semanticIntelligence.aiAgent.detail.index
                                                        .pleaseEnter
                                                }
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="APIToken"
                                            name="apiKey"
                                            rules={[{ required: true }]}
                                        >
                                            <Input
                                                maxLength={50}
                                                placeholder={
                                                    /* 请输入 */ UdeskLocales['current'].pages
                                                        .semanticIntelligence.aiAgent.detail.index
                                                        .pleaseEnter
                                                }
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label={
                                                /* user标识 */ UdeskLocales['current'].pages
                                                    .semanticIntelligence.aiAgent.detail.index
                                                    .userID
                                            }
                                            name="user"
                                            rules={[{ required: true }]}
                                        >
                                            <Input
                                                maxLength={50}
                                                placeholder={
                                                    /* 请输入 */ UdeskLocales['current'].pages
                                                        .semanticIntelligence.aiAgent.detail.index
                                                        .pleaseEnter
                                                }
                                            />
                                        </Form.Item>
                                    </>
                                ) : null;
                            }}
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 类型 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.type
                            }
                            name="typeSelect"
                            rules={[{ required: true }]}
                        >
                            <TypeSelect />
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 对话消息 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.conversationMessages
                            }
                            name="dialogText"
                            rules={[{ required: true }]}
                        >
                            <DialogMessage />
                        </Form.Item>

                        <Form.Item
                            label={
                                /* 变量 */ UdeskLocales['current'].pages.semanticIntelligence
                                    .aiAgent.detail.index.variable
                            }
                            name="variateAssociateList"
                            // rules={[{ required: true }]}
                        >
                            <ParamsList
                                dataSourceList={[
                                    Udesk.enums.inspectDataSources.voiceCall.id,
                                    Udesk.enums.inspectDataSources.textDialogue.id,
                                    Udesk.enums.inspectDataSources.ticket.id,
                                ]}
                            />
                        </Form.Item>

                        <Form.Item wrapperCol={{ offset: 4 }}>
                            <Space>
                                <Button loading={loading && { delay: 200 }} onClick={onCancel}>
                                    {UdeskLocales['current'].labels.cancel}
                                </Button>

                                <Button
                                    loading={loading && { delay: 200 }}
                                    type="primary"
                                    onClick={onFinish}
                                >
                                    {UdeskLocales['current'].labels.save}
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Spin>
            </Wrapper>
        </Page>
    );
});

class Component extends React.Component {
    render() {
        return <Template {...this.props} />;
    }
}

export default Udesk.react.udeskify(Component);
